import { DateRangeType } from "@/types/question";

/**
 * Parsea string "YYYY-MM-DD" (ex: input type="date") como meia-noite no fuso local.
 * Evita que new Date("2025-02-19") seja interpretado como UTC (que no Brasil vira 18/02).
 */
export function parseDateStringAsLocal(dateStr: string): Date {
	const [y, m, d] = dateStr.split("-").map(Number);
	if (
		y === undefined ||
		m === undefined ||
		d === undefined ||
		Number.isNaN(y) ||
		Number.isNaN(m) ||
		Number.isNaN(d)
	) {
		return new Date(Number.NaN);
	}
	return new Date(y, m - 1, d);
}

/**
 * Formata um Date para "YYYY-MM-DD" usando o dia no fuso local (para input type="date").
 */
export function formatDateToLocalYYYYMMDD(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

/**
 * Interpreta um Date como "só data" (parte UTC) e retorna meia-noite local nesse dia.
 * Corrige exibição quando a data veio da API em UTC (ex: 19/02 00:00 UTC vira 19/02 00:00 local).
 */
export function dateOnlyToLocalMidnight(date: Date): Date {
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function sanitizeNumericInput(value: string): string {
	const cleaned = value.replace(/[^\d]/g, "");
	return cleaned;
}

export function parseNumericInput(value: string): number | undefined {
	const cleaned = sanitizeNumericInput(value);
	if (cleaned === "") {
		return;
	}

	const num = Number.parseInt(cleaned, 10);
	return Number.isNaN(num) ? undefined : num;
}

type ValidateDateInputParams = {
	value?: string;
	dateRangeType?: DateRangeType;
	today: Date;
	existingMinDate?: Date;
	existingMaxDate?: Date;
	setError?: (error: string | null) => void;
};

export const validateDateInput = ({
	value,
	dateRangeType,
	today,
	existingMinDate,
	existingMaxDate,
	setError,
}: ValidateDateInputParams): Date | undefined => {
	if (!value) {
		return;
	}

	const inputDate = parseDateStringAsLocal(value);
	if (Number.isNaN(inputDate.getTime())) {
		return;
	}
	inputDate.setHours(0, 0, 0, 0);

	const normalizedToday = new Date(today);
	normalizedToday.setHours(0, 0, 0, 0);

	if (
		dateRangeType === DateRangeType.FutureOnly &&
		inputDate <= normalizedToday
	) {
		setError?.("Para 'Apenas futuro', a data deve ser posterior a hoje.");
		return;
	}

	if (
		dateRangeType === DateRangeType.PastOnly &&
		inputDate >= normalizedToday
	) {
		setError?.("Para 'Apenas passado', a data deve ser anterior a hoje.");
		return;
	}

	if (dateRangeType === DateRangeType.Range) {
		if (existingMaxDate && inputDate > existingMaxDate) {
			setError?.("A data mínima não pode ser maior que a data máxima.");
			return;
		}

		if (existingMinDate && inputDate < existingMinDate) {
			setError?.("A data máxima não pode ser menor que a data mínima.");
			return;
		}
	}

	setError?.(null);
	return inputDate;
};
